import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, ShoppingBag } from 'lucide-react';

const STORAGE_KEY = 'tudorballs_welcome_seen_v1';

export default function WelcomeDialog() {
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(true);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const close = () => {
    if (dontShow) {
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md" data-testid="welcome-dialog">
        <DialogHeader className="items-center text-center">
          <div className="w-16 h-16 mb-2 rounded-2xl bg-slate-900 flex items-center justify-center shadow-md" data-testid="welcome-logo">
            <img
              src="/logo.png"
              alt="Tudor Balls"
              className="w-12 h-12 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextSibling.style.display = 'flex';
              }}
            />
            <Sparkles className="w-6 h-6 text-white hidden" />
          </div>
          <DialogTitle className="text-xl font-semibold tracking-tight">Welcome to Tudor Balls!</DialogTitle>
          <DialogDescription className="sr-only">Welcome message for the Tudor Balls ball designer</DialogDescription>
        </DialogHeader>
        <div className="text-sm text-slate-600 leading-relaxed space-y-3" data-testid="welcome-body">
          <p>
            Step into the designer and build your dream ball from scratch. Whether you want a classic
            look or a wild geometric shape, you have total control. Grab a vertex, tweak the seams,
            splash on some color, and watch your design come to life.
          </p>
          <p className="text-xs text-slate-500 italic">
            Note that if you push the panels to their absolute max there might be some issues when
            creating the cut vectors. We are working on it.
          </p>
        </div>
        <label className="flex items-center gap-2 mt-2 text-xs text-slate-500 select-none" data-testid="welcome-dontshow">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="w-4 h-4 accent-slate-900"
          />
          Don&apos;t show again
        </label>
        <Button
          onClick={close}
          className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="welcome-cta-start"
        >
          Start Designing
        </Button>
        <a
          href="https://shop.tudorballs.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={close}
          data-testid="welcome-cta-shop"
          className="group mt-3 flex items-center justify-center gap-3 w-full text-lg sm:text-xl font-semibold text-white bg-gradient-to-r from-[#007AFF] to-[#0062CC] hover:from-[#0062CC] hover:to-[#004a99] rounded-2xl px-5 py-4 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 leading-tight"
        >
          <ShoppingBag className="w-6 h-6 shrink-0 transition-transform group-hover:scale-110" />
          Check out our online store for hand made balls
        </a>
      </DialogContent>
    </Dialog>
  );
}
